/**
 * 社内（顧客管理）の手当・賃金テーブル。公式の値が存在しないためサンプル値。
 *
 * 法令ではなく顧客の賃金規程が定める表なので、法定基準一覧ではなく参照先カタログに
 * `coreSource: 'in-house'` として載る。行 = 照会キー（資格・等級 × 号俸・役職・扶養区分）で
 * worker を持たないため、コア DB 抽出 3 表の行突合の対象外。
 */

import type { PayrollReferenceCatalogTable } from '../../types';

/** 技術手当テーブル。個人資格保有情報に対応する手当月額（サンプル） */
export const skillAllowanceTable: PayrollReferenceCatalogTable = {
  columns: [{
    label: '資格'
  }, {
    label: '区分'
  }, {
    align: 'end',
    label: '手当月額（円）'
  }],
  rows: [['基本情報技術者', 'IT 基礎', '5,000'], ['応用情報技術者', 'IT 応用', '10,000'], ['データベーススペシャリスト', 'IT 高度', '15,000'], ['ネットワークスペシャリスト', 'IT 高度', '15,000'], ['情報処理安全確保支援士', 'IT 高度（登録）', '20,000'], ['プロジェクトマネージャ', 'IT 高度', '25,000'], ['第一種電気工事士', '設備', '12,000'], ['日商簿記検定 1 級', '経理', '10,000'], ['社会保険労務士', '労務（登録）', '20,000']]
};
/** 号俸表（賃金テーブル）。等級 × 号俸の 1 組に対応する基本給（サンプル） */
export const wageGradeTable: PayrollReferenceCatalogTable = {
  columns: [{
    label: '等級'
  }, {
    label: '号俸'
  }, {
    align: 'end',
    label: '基本給（円）'
  }],
  rows: [['1 等級', '1 号俸', '180,000'], ['1 等級', '2 号俸', '183,000'], ['1 等級', '3 号俸', '186,000'], ['1 等級', '4 号俸', '189,000'], ['1 等級', '5 号俸', '192,000'], ['2 等級', '1 号俸', '200,000'], ['2 等級', '2 号俸', '203,500'], ['2 等級', '3 号俸', '207,000'], ['2 等級', '4 号俸', '210,500'], ['2 等級', '5 号俸', '214,000'], ['3 等級', '1 号俸', '225,000'], ['3 等級', '2 号俸', '229,000'], ['3 等級', '3 号俸', '233,000'], ['3 等級', '4 号俸', '237,000'], ['3 等級', '5 号俸', '241,000'], ['4 等級', '1 号俸', '255,000'], ['4 等級', '2 号俸', '259,500'], ['4 等級', '3 号俸', '264,000'], ['4 等級', '4 号俸', '268,500'], ['4 等級', '5 号俸', '273,000'], ['5 等級', '1 号俸', '290,000'], ['5 等級', '2 号俸', '295,000'], ['5 等級', '3 号俸', '300,000'], ['5 等級', '4 号俸', '305,000'], ['5 等級', '5 号俸', '310,000'], ['6 等級', '1 号俸', '330,000'], ['6 等級', '2 号俸', '335,500'], ['6 等級', '3 号俸', '341,000'], ['6 等級', '4 号俸', '346,500'], ['6 等級', '5 号俸', '352,000']]
};
/** 役職手当 単価表。役職に応じた手当月額（サンプル） */
export const positionAllowanceTable: PayrollReferenceCatalogTable = {
  columns: [{
    label: '役職'
  }, {
    align: 'end',
    label: '手当月額（円）'
  }],
  rows: [['部長', '80,000'], ['次長', '60,000'], ['課長', '45,000'], ['係長', '25,000'], ['主任', '15,000']]
};
/** 家族手当 単価表。扶養区分に応じた手当単価（サンプル） */
export const familyAllowanceTable: PayrollReferenceCatalogTable = {
  columns: [{
    label: '扶養区分'
  }, {
    align: 'end',
    label: '手当月額（円）'
  }, {
    label: '支給上限'
  }],
  rows: [['配偶者', '13,000', '1 名'], ['子（第 1 子）', '8,000', '—'], ['子（第 2 子）', '8,000', '—'], ['子（第 3 子以降）', '5,000', '—'], ['その他の扶養親族', '5,000', '2 名']]
};

/** 査定ランク係数変換表。人事評価の査定ランクを賞与の評価係数へ変換する（サンプル） */
export const bonusEvaluationCoefficientTable: PayrollReferenceCatalogTable = {
  columns: [{
    label: '査定ランク'
  }, {
    label: '査定点（下限以上）'
  }, {
    align: 'end',
    label: '賞与評価係数'
  }],
  rows: [['S', '90.0 点', '1.50'], ['A', '80.0 点', '1.25'], ['B', '70.0 点', '1.00'], ['C', '60.0 点', '0.85'], ['D', '0.0 点', '0.70']]
};

/** 賞与ポイント表。等級 × 査定ランクに対応する賞与ポイント（サンプル） */
export const bonusPointTable: PayrollReferenceCatalogTable = {
  columns: [{
    label: '等級'
  }, {
    label: '想定職位'
  }, {
    align: 'end',
    label: 'S'
  }, {
    align: 'end',
    label: 'A'
  }, {
    align: 'end',
    label: 'B'
  }, {
    align: 'end',
    label: 'C'
  }, {
    align: 'end',
    label: 'D'
  }],
  rows: [['5 等級', '部長', '5.20', '4.60', '4.00', '3.40', '2.80'], ['4 等級', '課長', '3.90', '3.45', '3.00', '2.55', '2.10'], ['3 等級', '係長', '2.60', '2.30', '2.00', '1.70', '1.40'], ['2 等級', '一般職（上級）', '1.82', '1.61', '1.40', '1.19', '0.98'], ['1 等級', '一般職（初級）', '1.20', '1.10', '1.00', '0.90', '0.80']]
};